import type { AssetId } from '../../assets/AssetTypes';
import type { DecorationSpacing } from './DecorationCatalog';

interface Claim {
  readonly owner: number;
  readonly assetId: AssetId;
  readonly x: number;
  readonly z: number;
  readonly spacing: DecorationSpacing;
}

/**
 * Ground space claimed by backdrop decorations of the live segments. Backdrop pieces stand
 * 15–35 m beside their own segment, so around a turn they can reach the pieces of the next
 * segments – the first one shown keeps its place, later overlapping ones are skipped.
 * Pure bookkeeping (no engine types), owners are segment uids.
 */
export class DecorationSpacingRegistry {
  private readonly claims: Claim[] = [];

  /** Claims the space if it is free; returns false (nothing claimed) on overlap. */
  tryClaim(owner: number, assetId: AssetId, x: number, z: number, spacing: DecorationSpacing): boolean {
    for (const other of this.claims) {
      const distance = Math.hypot(other.x - x, other.z - z);
      if (distance < other.spacing.radius + spacing.radius) return false;
      if (other.assetId === assetId) {
        const minimum = Math.max(other.spacing.sameAssetDistance ?? 0, spacing.sameAssetDistance ?? 0);
        if (distance < minimum) return false;
      }
    }
    this.claims.push({ owner, assetId, x, z, spacing });
    return true;
  }

  release(owner: number): void {
    for (let i = this.claims.length - 1; i >= 0; i--) {
      if (this.claims[i]?.owner === owner) this.claims.splice(i, 1);
    }
  }

  get size(): number {
    return this.claims.length;
  }
}
